<template>
  <div>
    <headerSection></headerSection>
    <div id="main__content">
      <div class="x-main-container">
        <div class="x-main-content">
          <div class="worldcup-ranking" :style="worldCupThemeStyle">

            <!-- Hero Header -->
            <div class="ranking-hero">
              <div class="hero-bg"></div>
              <div class="hero-content">
                <div class="hero-trophy">⚽🏆</div>
                <h1 class="hero-title">WORLD CUP<br>RANKING</h1>
                <div class="hero-date">
                  <span class="date-badge">ฟุตบอลโลก 11 มิ.ย. – 19 ก.ค. 2026</span>
                </div>
              </div>
            </div>

            <!-- Milestone panel -->
            <div class="milestone-panel">
              <div class="panel-header">
                <span class="panel-title">🎁 สะสมแต้มแลกเหรียญ</span>
                <span class="panel-count">แต้มของฉัน: <strong>{{ myPoints.toLocaleString() }}</strong></span>
              </div>
              <div class="milestone-list">
                <div v-for="tier in milestones" :key="tier.points" class="milestone-item" :class="{ 'ms-claimed': isClaimed(tier), 'ms-ready': canClaimTier(tier) }">
                  <div class="ms-info">
                    <div class="ms-points">ครบ {{ tier.points }} แต้ม</div>
                    <div class="ms-coins">🪙 {{ tier.coins }} เหรียญ</div>
                  </div>
                  <div class="ms-progress">
                    <div class="ms-bar"><div class="ms-fill" :style="{ width: progressPct(tier.points) + '%' }"></div></div>
                  </div>
                  <div class="ms-action">
                    <span v-if="isClaimed(tier)" class="ms-done">รับแล้ว ✅</span>
                    <button v-else-if="canClaimTier(tier)" class="btn-claim-ms" :disabled="claiming" @click="claimMilestone(tier)">
                      {{ claiming ? '...' : 'กดรับ +' + tier.coins }}
                    </button>
                    <span v-else-if="claimDeadlinePassed" class="ms-locked">หมดเวลา</span>
                    <span v-else class="ms-locked">อีก {{ (tier.points - myPoints).toLocaleString() }}</span>
                  </div>
                </div>
              </div>
              <div class="ms-note">รับเหรียญได้ถึง 21 ก.ค. 2026 เวลา 23:59 น. · รางวัลแต่ละขั้นกดรับได้เต็มจำนวนเมื่อแต้มถึงขั้น · ทายผลถูก +1 / สกอร์เป๊ะ +3</div>
            </div>

            <!-- Ranking list -->
            <div class="ranking-panel">
              <div class="panel-header">
                <span class="panel-title">🏅 กระดานคะแนน</span>
                <span class="panel-count" v-if="rankings.length">{{ rankings.length }} คน</span>
              </div>

              <div v-if="loading" class="ranking-loading">
                <div class="loading-spinner"></div>
                <span>กำลังโหลด...</span>
              </div>
              <div v-else-if="rankings.length === 0" class="ranking-empty">
                <div class="empty-icon">⚽</div>
                <div class="empty-text">ยังไม่มีผู้เข้าร่วม</div>
                <div class="empty-sub">ทายผลบอลโลกเพื่อขึ้นกระดาน!</div>
              </div>

              <div v-else class="ranking-body">
                <!-- Top 3 podium -->
                <div v-if="rankings.length >= 3" class="podium">
                  <div class="podium-item podium-2" v-if="rankings[1]">
                    <div class="podium-medal">🥈</div>
                    <div class="podium-code">{{ maskCode(rankings[1].code) }}</div>
                    <div class="podium-score">{{ (rankings[1].totalPoints || 0).toLocaleString() }}</div>
                    <div class="podium-bar bar-2"></div>
                  </div>
                  <div class="podium-item podium-1" v-if="rankings[0]">
                    <div class="podium-crown">👑</div>
                    <div class="podium-medal">🥇</div>
                    <div class="podium-code">{{ maskCode(rankings[0].code) }}</div>
                    <div class="podium-score">{{ (rankings[0].totalPoints || 0).toLocaleString() }}</div>
                    <div class="podium-bar bar-1"></div>
                  </div>
                  <div class="podium-item podium-3" v-if="rankings[2]">
                    <div class="podium-medal">🥉</div>
                    <div class="podium-code">{{ maskCode(rankings[2].code) }}</div>
                    <div class="podium-score">{{ (rankings[2].totalPoints || 0).toLocaleString() }}</div>
                    <div class="podium-bar bar-3"></div>
                  </div>
                </div>

                <!-- Full ranking list -->
                <div
                  v-for="(item, index) in rankings"
                  :key="item.id"
                  class="ranking-item"
                  :class="{
                    'rank-top5': (index + 1) <= 5,
                    'rank-top10': (index + 1) > 5 && (index + 1) <= 10,
                    'rank-normal': (index + 1) > 10,
                    'rank-me': item.userkey === user.key
                  }"
                >
                  <div class="rank-number" :class="{'num-top5': (index + 1) <= 5, 'num-top10': (index + 1) > 5 && (index + 1) <= 10}">
                    <span v-if="index === 0">🥇</span>
                    <span v-else-if="index === 1">🥈</span>
                    <span v-else-if="index === 2">🥉</span>
                    <span v-else>{{ index + 1 }}</span>
                  </div>
                  <div class="rank-info">
                    <div class="rank-code">
                      {{ maskCode(item.code) }}
                      <span v-if="item.userkey === user.key" class="me-badge">You</span>
                    </div>
                    <div class="rank-detail">ทาย {{ item.predictionsCount || 0 }} คู่ · ผลถูก {{ item.correctResults || 0 }} · เป๊ะ {{ item.correctScores || 0 }}</div>
                  </div>
                  <div class="rank-score" :class="{'score-top5': (index + 1) <= 5, 'score-top10': (index + 1) > 5 && (index + 1) <= 10}">{{ (item.totalPoints || 0).toLocaleString() }}</div>
                </div>
              </div>
            </div>

            <div class="ranking-footer">
              <button class="btn-go-predict" @click="$router.push('/worldcup')">⚽ ไปทายผลบอล</button>
              <button class="btn-back" @click="$router.push('/')">← กลับหน้าหลัก</button>
            </div>
          </div>
        </div>
      </div>
      <footerSection></footerSection>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/firestore'
import 'firebase/database'
import 'firebase/auth'
import 'firebase/functions'
import router from './../router'
import moment from 'moment-timezone'

const WORLD_CUP_CLAIM_DEADLINE = '2026-07-21 23:59:59'

export default {
  name: 'WorldCupRanking',
  components: {
    headerSection: () => import('./Header.vue'),
    footerSection: () => import('./Footer.vue')
  },
  created () {
    const user = localStorage.getItem('userData')
    if (user) {
      const _user = JSON.parse(user)
      this.$store.dispatch('user/setUser', _user).then(() => {
        this.loadRanking()
      }).catch(err => { console.error(err) })
    } else {
      firebase.auth().signOut()
      localStorage.removeItem('userData')
      router.replace('/login')
    }
  },
  mounted () {
    this.backgroundImage = require('../assets/images/Shiba-BG.jpg')
  },
  computed: {
    worldCupThemeStyle () {
      return {
        'background-image': `url(${this.backgroundImage})`,
        'background-position': 'center',
        'background-repeat': 'no-repeat',
        'background-size': 'cover',
        '--wc-primary': '#f2c94c',
        '--wc-secondary': '#1856b3',
        '--wc-accent': '#fff4bf',
        '--wc-deep': '#10183f',
        '--wc-primary-rgb': '242, 201, 76',
        '--wc-secondary-rgb': '24, 86, 179'
      }
    },
    user () {
      return this.$store.state.user.user
    },
    myPoints () {
      return (this.myTotal && this.myTotal.totalPoints) ? this.myTotal.totalPoints : 0
    },
    paidCoins () {
      return (this.myTotal && this.myTotal.milestoneCoinsPaid) ? this.myTotal.milestoneCoinsPaid : 0
    },
    claimedMilestones () {
      const saved = (this.myTotal && this.myTotal.milestoneClaims) ? this.myTotal.milestoneClaims : {}
      const savedKeys = Object.keys(saved)
      if (savedKeys.length > 0) {
        const savedClaims = {}
        savedKeys.forEach((key) => {
          if (saved[key]) savedClaims[String(key)] = true
        })
        return savedClaims
      }

      const claims = {}
      this.milestones.forEach((tier) => {
        if (tier.coins <= this.paidCoins) claims[String(tier.points)] = true
      })
      return claims
    },
    currentTier () {
      let tier = 0
      this.milestones.forEach((m) => { if (this.myPoints >= m.points) tier = m.points })
      return tier
    },
    claimDeadlinePassed () {
      return moment().tz('Asia/Bangkok').isAfter(moment.tz(WORLD_CUP_CLAIM_DEADLINE, 'YYYY-MM-DD HH:mm:ss', 'Asia/Bangkok'))
    }
  },
  data () {
    return {
      backgroundImage: '',
      loading: true,
      rankings: [],
      myTotal: null,
      claiming: false,
      milestones: [
        { points: 10, coins: 20 },
        { points: 25, coins: 40 },
        { points: 50, coins: 100 },
        { points: 80, coins: 200 },
        { points: 120, coins: 500 }
      ]
    }
  },
  methods: {
    async loadRanking () {
      this.loading = true
      const snapshot = await firebase.firestore().collection('worldcup_predictions_total')
        .orderBy('totalPoints', 'desc')
        .limit(100)
        .get()

      this.rankings = []
      snapshot.forEach((doc) => {
        this.rankings.push({ id: doc.id, ...doc.data() })
      })

      // Tiebreaker: totalPoints desc → correctScores desc → correctResults desc → lastPickAt asc
      this.rankings.sort((a, b) => {
        if ((b.totalPoints || 0) !== (a.totalPoints || 0)) return (b.totalPoints || 0) - (a.totalPoints || 0)
        if ((b.correctScores || 0) !== (a.correctScores || 0)) return (b.correctScores || 0) - (a.correctScores || 0)
        if ((b.correctResults || 0) !== (a.correctResults || 0)) return (b.correctResults || 0) - (a.correctResults || 0)
        return (a.lastPickAt || '').localeCompare(b.lastPickAt || '')
      })

      const mine = this.rankings.find(item => item.userkey === this.user.key)
      if (mine) {
        this.myTotal = mine
      } else {
        const myDoc = await firebase.firestore().collection('worldcup_predictions_total').doc(this.user.key).get()
        this.myTotal = myDoc.exists ? myDoc.data() : null
      }

      this.loading = false
    },
    maskCode (code) {
      return code || '***'
    },
    isClaimed (tier) {
      return !!this.claimedMilestones[String(tier.points)]
    },
    canClaimTier (tier) {
      return !this.claimDeadlinePassed && this.myPoints >= tier.points && !this.isClaimed(tier)
    },
    progressPct (points) {
      return Math.min(100, Math.round((this.myPoints / points) * 100))
    },
    async claimMilestone (tier) {
      if (this.claiming || this.currentTier === 0) return
      this.claiming = true
      try {
        const claimFn = firebase.app().functions('asia-east2').httpsCallable('claimWorldCupMilestone')
        const result = await claimFn({ userkey: this.user.key, tier: tier.points })
        if (result.data.success) {
          this.$set(this.myTotal, 'milestoneCoinsPaid', result.data.totalPaid)
          this.$set(this.myTotal, 'milestoneClaims', result.data.claimedTiers || Object.assign({}, this.claimedMilestones, { [String(result.data.tier)]: true }))
          this.$swal({
            icon: 'success',
            title: 'รับรางวัลสำเร็จ!',
            text: `ได้รับ ${result.data.coins} เหรียญ (ขั้น ${result.data.tier} แต้ม)`
          })
        }
      } catch (err) {
        let msg = 'เกิดข้อผิดพลาด'
        if (err.code === 'already-exists') msg = 'รับของขั้นนี้ไปแล้ว'
        else if (err.code === 'failed-precondition' && err.message && err.message.indexOf('claiming ended') !== -1) msg = 'หมดเวลาแลกเหรียญบอลโลกแล้ว'
        else if (err.code === 'failed-precondition') msg = 'ต้องสะสมอย่างน้อย 10 แต้มก่อน'
        else if (err.code === 'not-found') msg = 'ยังไม่มีประวัติการทาย'
        this.$swal({ icon: 'error', title: 'ไม่สำเร็จ', text: msg })
      } finally {
        this.claiming = false
      }
    }
  }
}
</script>

<style scoped>
.worldcup-ranking {
  min-height: 100vh;
  padding-bottom: 80px;
}

/* ========== HERO HEADER ========== */
.ranking-hero {
  position: relative;
  overflow: hidden;
  margin: 12px;
  border-radius: 24px;
  border: 2px solid rgba(var(--wc-secondary-rgb),0.35);
}
.hero-bg {
  position: absolute; inset: 0;
  background: linear-gradient(145deg, var(--wc-deep) 0%, var(--wc-secondary) 45%, var(--wc-secondary) 100%);
  opacity: 0.95;
}
.hero-content {
  position: relative;
  text-align: center;
  padding: 28px 20px 22px;
}
.hero-trophy {
  font-size: 50px;
  filter: drop-shadow(0 4px 12px rgba(255,215,0,0.4));
  animation: trophyFloat 3s ease-in-out infinite;
}
@keyframes trophyFloat {
  0%,100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
.hero-title {
  font-size: 28px;
  font-weight: 900;
  letter-spacing: 3px;
  background: linear-gradient(135deg, var(--wc-primary), var(--wc-primary), var(--wc-primary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  margin: 8px 0 12px;
}
.date-badge {
  display: inline-block;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 20px;
  padding: 4px 16px;
  font-size: 13px;
  color: rgba(255,255,255,0.85);
  font-weight: 600;
}

/* ========== MILESTONE PANEL ========== */
.milestone-panel {
  margin: 0 12px 12px;
  background: linear-gradient(180deg, rgba(15,30,15,0.93), rgba(8,18,10,0.95));
  backdrop-filter: blur(12px);
  border-radius: 24px;
  border: 2px solid rgba(120,255,170,0.18);
  overflow: hidden;
}
.milestone-list { padding: 4px 12px 8px; }
.milestone-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin: 6px 0;
  border-radius: 14px;
  border: 1.5px solid rgba(255,255,255,0.07);
  background: rgba(255,255,255,0.03);
}
.ms-ready {
  border-color: rgba(255,215,0,0.5);
  background: linear-gradient(135deg, rgba(255,215,0,0.12), rgba(255,140,0,0.06));
  box-shadow: 0 0 14px rgba(255,200,0,0.12);
}
.ms-claimed { opacity: 0.65; }
.ms-info { min-width: 86px; }
.ms-points { font-size: 14px; font-weight: 900; color: #fff; }
.ms-coins { font-size: 12px; font-weight: 700; color: var(--wc-primary); margin-top: 1px; }
.ms-progress { flex: 1; }
.ms-bar {
  height: 8px;
  background: rgba(255,255,255,0.08);
  border-radius: 6px;
  overflow: hidden;
}
.ms-fill {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #0fbf5f, var(--wc-primary));
  transition: width 0.4s ease;
}
.ms-action { min-width: 74px; text-align: right; }
.ms-done { font-size: 12px; font-weight: 700; color: #aef5cd; }
.ms-locked { font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.35); }
.btn-claim-ms {
  font-size: 13px; font-weight: 800;
  padding: 7px 16px;
  border: none; border-radius: 50px;
  background: linear-gradient(135deg, var(--wc-primary), #FF8C00);
  color: #1a1a1a; cursor: pointer;
  box-shadow: 0 3px 12px rgba(255,140,0,0.45);
  animation: claimPulse 1.6s ease infinite;
}
@keyframes claimPulse {
  0%,100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}
.btn-claim-ms:disabled { opacity: 0.5; animation: none; }
.ms-note {
  padding: 4px 16px 14px;
  font-size: 11px;
  color: rgba(255,255,255,0.5);
  text-align: center;
}
.ms-link { color: var(--wc-secondary); font-weight: 700; cursor: pointer; }

/* ========== RANKING PANEL ========== */
.ranking-panel {
  margin: 0 12px;
  background: linear-gradient(180deg, rgba(10,15,40,0.92), rgba(5,10,30,0.95));
  backdrop-filter: blur(12px);
  border-radius: 24px;
  border: 2px solid rgba(255,255,255,0.08);
  overflow: hidden;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.panel-title { font-size: 16px; font-weight: 800; color: #fff; }
.panel-count {
  font-size: 12px;
  background: rgba(255,255,255,0.08);
  padding: 3px 10px;
  border-radius: 10px;
  color: rgba(255,255,255,0.6);
  font-weight: 600;
}
.panel-count strong { color: var(--wc-primary); }

/* Loading & Empty */
.ranking-loading {
  text-align: center;
  color: rgba(255,255,255,0.6);
  padding: 50px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.loading-spinner {
  width: 32px; height: 32px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top-color: var(--wc-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.ranking-empty {
  text-align: center;
  padding: 50px 20px;
}
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-text { color: #fff; font-size: 18px; font-weight: 700; }
.empty-sub { color: rgba(255,255,255,0.5); font-size: 13px; margin-top: 4px; }

/* ========== PODIUM (Top 3) ========== */
.podium {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 6px;
  padding: 20px 16px 0;
}
.podium-item {
  flex: 1;
  max-width: 110px;
  text-align: center;
}
.podium-crown {
  font-size: 22px;
  animation: crownBounce 2s ease infinite;
}
@keyframes crownBounce {
  0%,100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
.podium-medal { font-size: 30px; }
.podium-code {
  font-size: 12px;
  color: #fff;
  font-weight: 700;
  margin: 4px 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.podium-score {
  font-size: 16px;
  font-weight: 900;
  color: var(--wc-primary);
  text-shadow: 0 0 8px rgba(255,215,0,0.3);
  margin-bottom: 8px;
}
.podium-bar {
  border-radius: 8px 8px 0 0;
  margin: 0 auto;
  width: 100%;
}
.bar-1 {
  height: 80px;
  background: linear-gradient(180deg, var(--wc-primary), #c8960c);
  box-shadow: 0 0 20px rgba(255,215,0,0.3);
}
.bar-2 {
  height: 56px;
  background: linear-gradient(180deg, #C0C0C0, #8a8a8a);
  box-shadow: 0 0 15px rgba(192,192,192,0.2);
}
.bar-3 {
  height: 40px;
  background: linear-gradient(180deg, #CD7F32, #8a5520);
  box-shadow: 0 0 15px rgba(205,127,50,0.2);
}

.ranking-body { padding-bottom: 10px; }

/* ========== RANKING ITEMS ========== */
.ranking-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin: 6px 10px;
  border-radius: 12px;
  border: 1.5px solid;
  transition: background 0.2s;
}
.rank-top5 {
  background: linear-gradient(135deg, rgba(255,215,0,0.1), rgba(255,180,0,0.05));
  border-color: rgba(255,215,0,0.3);
  border-left: 3px solid var(--wc-primary);
}
.rank-top10 {
  background: linear-gradient(135deg, rgba(160,200,240,0.08), rgba(120,160,200,0.04));
  border-color: rgba(160,200,240,0.2);
  border-left: 3px solid #90b8e0;
}
.rank-normal {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.04);
}
.rank-me {
  border-color: rgba(0,200,255,0.5) !important;
  background: rgba(0,120,255,0.12) !important;
  box-shadow: inset 0 0 20px rgba(0,150,255,0.08), 0 0 12px rgba(0,200,255,0.15);
  border-left: 3px solid #00c8ff !important;
}
.rank-number {
  width: 30px;
  text-align: center;
  font-size: 15px;
  font-weight: 900;
  color: rgba(255,255,255,0.35);
}
.num-top5 {
  color: var(--wc-primary);
  font-size: 17px;
  text-shadow: 0 0 8px rgba(255,215,0,0.3);
}
.num-top10 {
  color: #90b8e0;
  font-size: 16px;
}
.rank-info { flex: 1; min-width: 0; }
.rank-code { color: #fff; font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 6px; }
.me-badge {
  font-size: 10px; font-weight: 800;
  background: linear-gradient(135deg, #00c8ff, #0080ff);
  color: #fff; padding: 1px 8px; border-radius: 10px;
}
.rank-detail { color: rgba(255,255,255,0.4); font-size: 10px; margin-top: 2px; }
.rank-score {
  font-size: 18px;
  font-weight: 900;
  color: rgba(255,255,255,0.5);
  min-width: 50px;
  text-align: right;
}
.score-top5 {
  color: var(--wc-primary);
  font-size: 20px;
  text-shadow: 0 0 10px rgba(255,215,0,0.3);
}
.score-top10 {
  color: #90b8e0;
  font-size: 19px;
}

/* ========== FOOTER ========== */
.ranking-footer {
  text-align: center;
  padding: 24px 16px;
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}
.btn-go-predict {
  font-size: 15px; font-weight: 800;
  padding: 12px 28px;
  border: none; border-radius: 50px;
  background: linear-gradient(135deg, #0fbf5f, #0a8d3a);
  color: #fff; cursor: pointer;
  box-shadow: 0 4px 18px rgba(0,180,90,0.4);
}
.btn-back {
  font-size: 15px; font-weight: 700;
  padding: 12px 28px;
  border: 1.5px solid rgba(255,255,255,0.2);
  border-radius: 50px;
  background: linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06));
  color: #fff; cursor: pointer;
  backdrop-filter: blur(8px);
}
.btn-back:active { background: rgba(255,255,255,0.18); }

@media (max-width: 380px) {
  .hero-title { font-size: 22px; letter-spacing: 2px; }
  .podium-item { max-width: 90px; }
  .ms-info { min-width: 72px; }
}
</style>
